/** Product policy for a NEW password effect only. Historical login and original
 * completed receipts do not call this validator. Secrets always stay exact. */
export const NEW_PASSWORD_POLICY = Object.freeze({
  version: 3 as const,
  minCodePoints: 6,
  maxCodePoints: 50,
  maxCodeUnits: 50,
});
// Preserve bounded retries of receipts completed under the previous policy.
// This limit never admits a NEW password effect.
export const HISTORICAL_PASSWORD_INPUT_MAX_CODE_UNITS = 256;

/** Finite local list, versioned with the policy. This is not a complete breach
 * corpus or a strength certificate. Comparison never changes the Auth secret.
 */
export const BLOCKED_NEW_PASSWORDS = Object.freeze([
  "123456",
  "1234567",
  "12345678",
  "123456789",
  "1234567890",
  "password",
  "qwerty",
  "qwerty123",
  "abcdef",
  "abc123",
  "111111",
  "000000",
  "aaaaaa",
  "letmein",
  "admin123",
  "contraseña",
  "contrasena",
  "alpelo",
  "alpelo123",
  "123456789012345",
  "1234567890123456",
  "12345678901234567890",
  "0123456789012345",
  "0987654321098765",
  "passwordpassword",
  "password123456789",
  "passwordpassword1",
  "qwertyuiopasdfgh",
  "qwertyuiop123456",
  "abcdefghijklmnop",
  "letmeinletmein123",
  "000000000000000",
  "111111111111111",
  "aaaaaaaaaaaaaaa",
  "alpeloalpeloalpelo",
  "alpeloelitealpeloelite",
  "barberprobarberpro",
]);

export type PasswordIssue =
  | "TYPE"
  | "LINE_BREAK"
  | "ENCODING"
  | "TOO_SHORT"
  | "TOO_LONG"
  | "BLOCKED";
/**
 * Select the safe user-facing message for a NEW-password policy failure.
 * @param {PasswordIssue} issue Failed policy check, never the password itself.
 * @return {string} Spanish message describing the applicable requirement.
 */
export function newPasswordMessage(issue: PasswordIssue): string {
  if (issue === "TYPE") return "Introduce una contraseña válida.";
  if (issue === "LINE_BREAK") {
    return "La contraseña no puede contener saltos de línea.";
  }
  if (issue === "ENCODING") {
    return "La contraseña contiene un carácter Unicode inválido.";
  }
  if (issue === "BLOCKED") {
    return "Elige una contraseña menos común. Puedes usar una frase larga.";
  }
  return (
    "La contraseña debe tener entre" +
    ` ${NEW_PASSWORD_POLICY.minCodePoints} y` +
    ` ${NEW_PASSWORD_POLICY.maxCodePoints} caracteres.`
  );
}
/** Fixed password-policy issue without retaining the submitted secret. */
export class NewPasswordPolicyError extends Error {
  /**
   * Construct the issue and its safe presentation message.
   * @param {PasswordIssue} issue Failed NEW-password policy check.
   */
  constructor(public readonly issue: PasswordIssue) {
    super(newPasswordMessage(issue));
    this.name = "NewPasswordPolicyError";
  }
}

/** Bounded representation only, for endpoints which must first decide NEW
 * versus
 * historical ACK. No minimum, blocklist or encoding recertification here.
 * @param {unknown} value Original password input, kept byte-for-byte as text.
 * @return {string} Bounded original input without new-effect certification.
 */
export function requirePasswordInput(value: unknown): string {
  if (typeof value !== "string") {
    throw new NewPasswordPolicyError("TYPE");
  }
  if (value.length > HISTORICAL_PASSWORD_INPUT_MAX_CODE_UNITS) {
    throw new NewPasswordPolicyError("TOO_LONG");
  }
  return value;
}

/**
 * Check NEW-effect bounds, line breaks, Unicode encoding and the finite list.
 * @param {unknown} value Original proposed password, never normalized for Auth.
 * @return {PasswordIssue|null} First failed check, or null when accepted.
 */
export function newPasswordIssue(value: unknown): PasswordIssue | null {
  if (typeof value !== "string") return "TYPE";
  if (value.length > NEW_PASSWORD_POLICY.maxCodeUnits) {
    return "TOO_LONG";
  }
  if (/[\r\n]/.test(value)) return "LINE_BREAK";
  let count = 0;
  for (let at = 0; at < value.length; at++, count++) {
    const unit = value.charCodeAt(at);
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const low = value.charCodeAt(++at);
      if (!(low >= 0xdc00 && low <= 0xdfff)) return "ENCODING";
    } else if (unit >= 0xdc00 && unit <= 0xdfff) return "ENCODING";
  }
  if (count < NEW_PASSWORD_POLICY.minCodePoints) return "TOO_SHORT";
  if (count > NEW_PASSWORD_POLICY.maxCodePoints) return "TOO_LONG";
  if (
    (BLOCKED_NEW_PASSWORDS as readonly string[]).includes(value.toLowerCase())
  ) {
    return "BLOCKED";
  }
  return null;
}
/**
 * Require the complete shared NEW-password policy before a new Auth effect.
 * @param {unknown} value Original proposed password.
 * @return {string} Exact accepted string, without trimming or case conversion.
 */
export function requireNewPassword(value: unknown): string {
  const issue = newPasswordIssue(value);
  if (issue) throw new NewPasswordPolicyError(issue);
  return value as string;
}
